import { statusOf, statusColor } from "@/lib/otStatus";
import { Flame, Droplets, Fan, Zap, Power } from "lucide-react";

function Gauge({ tag, x, y }) {
  if (!tag) return null;
  const st = statusOf(tag);
  const color = statusColor[st];
  const pct = Math.max(0, Math.min(100, ((tag.value - (tag.min ?? 0)) / ((tag.max ?? 100) - (tag.min ?? 0))) * 100));
  return (
    <div className="absolute -translate-x-1/2 -translate-y-1/2" style={{ left: x, top: y }} data-testid={`gauge-${tag.tag_id}`}>
      <div className="bg-popover border rounded-sm px-2.5 py-1.5 min-w-[112px]" style={{ borderColor: color + "66" }}>
        <div className="font-mono text-[10px] text-muted-foreground">{tag.tag_id}</div>
        <div className="font-mono text-base font-semibold leading-tight" style={{ color }}>
          {tag.value}<span className="text-[10px] text-muted-foreground ml-1">{tag.unit}</span>
        </div>
        <div className="h-1 bg-secondary rounded-full mt-1 overflow-hidden">
          <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, background: color }} />
        </div>
      </div>
    </div>
  );
}

function Equip({ tag, x, y, icon: Icon, onText = "RUNNING", spin }) {
  if (!tag) return null;
  const trip = tag.value === "TRIGGERED";
  const active = tag.value === onText;
  const color = trip ? statusColor.critical : active ? statusColor.normal : statusColor.inactive;
  return (
    <div className="absolute -translate-x-1/2 -translate-y-1/2 flex flex-col items-center gap-1" style={{ left: x, top: y }} data-testid={`equip-${tag.tag_id}`}>
      <div className="rounded-sm border-2 flex items-center justify-center bg-popover"
        style={{ borderColor: color, width: 54, height: 44, boxShadow: active ? `0 0 14px ${color}55` : "none" }}>
        <Icon className="w-5 h-5" style={{ color, animation: spin && active ? "pwrspin 1.1s linear infinite" : "none" }} />
      </div>
      <div className="font-mono text-[9px] text-muted-foreground whitespace-nowrap">{tag.tag_id}</div>
      <div className="font-mono text-[9px] whitespace-nowrap" style={{ color }}>{tag.value}</div>
    </div>
  );
}

export default function PowerMimic({ tags }) {
  const t = Object.fromEntries(tags.map((x) => [x.tag_id, x]));
  const synced = t["GEN-BRK"]?.value === "CLOSED" && t["TURB-01"]?.value === "RUNNING" && t["ESD-TRB"]?.value !== "TRIGGERED";
  const firing = t["FUEL-01"]?.value === "OPEN" && t["ESD-TRB"]?.value !== "TRIGGERED";

  return (
    <div className="bg-card border border-border/60 rounded-md overflow-hidden" data-testid="power-mimic">
      <style>{`@keyframes pwrspin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } } @keyframes pwrflow { from { background-position: 0 0; } to { background-position: 24px 0; } }`}</style>
      <div className="px-3 py-2 border-b border-border/60 bg-popover flex items-center justify-between">
        <span className="label-ot">Process Mimic · Steam Turbine Generating Unit</span>
        <span className="font-mono text-xs" style={{ color: synced ? statusColor.normal : statusColor.inactive }}>
          {synced ? "● ON GRID" : "○ OFF GRID"}
        </span>
      </div>
      <div className="relative w-full cr-grid" style={{ height: 420 }}>
        {/* steam header line: boiler -> turbine */}
        <div className="absolute" style={{ left: "20%", top: "40%", width: "30%", height: 8 }}>
          <div className="w-full h-full rounded-sm" style={{ background: "#1f1f24", border: "1px solid #2a2a30" }} />
          {firing && (
            <div className="absolute inset-0 rounded-sm opacity-70" style={{
              background: "repeating-linear-gradient(90deg,#FF9F0A 0 8px,transparent 8px 24px)",
              animation: "pwrflow 0.7s linear infinite",
            }} />
          )}
        </div>
        {/* shaft line: turbine -> generator */}
        <div className="absolute" style={{ left: "54%", top: "40%", width: "16%", height: 6 }}>
          <div className="w-full h-full rounded-sm" style={{ background: synced ? statusColor.normal + "55" : "#2a2a30" }} />
        </div>
        {/* tie line: generator -> grid */}
        <div className="absolute" style={{ left: "78%", top: "40%", width: "14%", height: 6 }}>
          <div className="w-full h-full rounded-sm" style={{ background: synced ? statusColor.normal : "#2a2a30" }} />
        </div>

        {/* boiler drum */}
        <div className="absolute -translate-x-1/2 -translate-y-1/2" style={{ left: "16%", top: "40%" }} data-testid="boiler-drum">
          {t["LT-DRM"] && (() => {
            const lvl = t["LT-DRM"].value;
            const st = statusOf(t["LT-DRM"]);
            return (
              <div className="flex flex-col items-center">
                <div className="w-16 h-20 border-2 border-border rounded-sm relative overflow-hidden bg-popover">
                  <div className="absolute bottom-0 left-0 w-full transition-all duration-500" style={{ height: `${lvl}%`, background: statusColor[st] + "55", borderTop: `2px solid ${statusColor[st]}` }} />
                  <div className="absolute inset-0 flex items-center justify-center font-mono text-[11px] font-semibold">{lvl}%</div>
                </div>
                <div className="font-mono text-[9px] text-muted-foreground mt-0.5">BOILER · LT-DRM</div>
              </div>
            );
          })()}
        </div>

        {/* equipment */}
        <Equip tag={t["FUEL-01"]} x="16%" y="74%" icon={Flame} onText="OPEN" />
        <Equip tag={t["BFP-01"]} x="6%" y="40%" icon={Droplets} onText="RUNNING" />
        <Equip tag={t["TURB-01"]} x="52%" y="40%" icon={Fan} onText="RUNNING" spin />
        <Equip tag={t["GEN-BRK"]} x="78%" y="40%" icon={Power} onText="CLOSED" />

        {/* generator */}
        <div className="absolute -translate-x-1/2 -translate-y-1/2 flex flex-col items-center gap-1" style={{ left: "66%", top: "40%" }} data-testid="generator-block">
          <div className="rounded-full border-2 flex items-center justify-center bg-popover"
            style={{ borderColor: synced ? statusColor.normal : statusColor.inactive, width: 56, height: 56, boxShadow: synced ? `0 0 16px ${statusColor.normal}55` : "none" }}>
            <Zap className="w-6 h-6" style={{ color: synced ? statusColor.normal : statusColor.inactive }} />
          </div>
          <div className="font-mono text-[9px] text-muted-foreground">GENERATOR</div>
        </div>

        {/* grid pylon */}
        <div className="absolute -translate-x-1/2 -translate-y-1/2 flex flex-col items-center" style={{ left: "94%", top: "40%" }}>
          <div className="font-mono text-[10px]" style={{ color: synced ? statusColor.normal : statusColor.inactive }}>⌁ GRID</div>
        </div>

        {/* gauges */}
        <Gauge tag={t["PT-BLR"]} x="16%" y="14%" />
        <Gauge tag={t["TT-STM"]} x="38%" y="14%" />
        <Gauge tag={t["SPD-TRB"]} x="52%" y="72%" />
        <Gauge tag={t["GEN-MW"]} x="78%" y="72%" />

        {/* trip chip */}
        {t["ESD-TRB"] && (
          <div className="absolute flex gap-2" style={{ right: "3%", top: "86%" }} data-testid="equip-ESD-TRB">
            <div className="flex items-center gap-1.5 px-2 py-1 rounded-sm border bg-popover"
              style={{ borderColor: (t["ESD-TRB"].value === "TRIGGERED" ? statusColor.critical : statusColor.normal) + "66" }}>
              <span className="w-2 h-2 rounded-full" style={{ background: t["ESD-TRB"].value === "TRIGGERED" ? statusColor.critical : statusColor.normal }} />
              <span className="font-mono text-[10px] text-muted-foreground">ESD-TRB</span>
              <span className="font-mono text-[10px] font-semibold" style={{ color: t["ESD-TRB"].value === "TRIGGERED" ? statusColor.critical : statusColor.normal }}>{t["ESD-TRB"].value}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
